import React from 'react';
import { Box, IconButton, Button } from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

export default function TablePaginationActions(props) {
    const { count, page, rowsPerPage, onPageChange } = props;
    const ref = React.useRef(null);

    React.useEffect(() => {
        if (ref.current) {
            const paginationRoot = ref.current.closest('.MuiTablePagination-root');
            if (paginationRoot) {
                if (!count || count <= 0) {
                    paginationRoot.style.display = 'none';
                } else {
                    paginationRoot.style.display = '';
                }
            }
        }
    }, [count]);

    if (!count || count <= 0) {
        return <span ref={ref} style={{ display: 'none' }} />;
    }

    const totalPages = Math.ceil(count / rowsPerPage) || 1;
    const currentPage = page + 1; // 1-indexed

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, totalPages - 1));
    };

    const handlePageClick = (event, targetPage) => {
        onPageChange(event, targetPage - 1);
    };

    const getPageNumbers = () => {
        if (totalPages <= 6) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        if (currentPage <= 3) {
            return [1, 2, 3, '...', totalPages];
        }
        if (currentPage >= totalPages - 2) {
            return [1, '...', totalPages - 2, totalPages - 1, totalPages];
        }
        return [1, '...', currentPage, '...', totalPages];
    };

    const pageNumbers = getPageNumbers();

    return (
        <Box ref={ref} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
                size="small"
                sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    borderRadius: 1.5,
                    border: '1px solid #eaecf0',
                    color: '#64748b',
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#d0d5dd' },
                    '&.Mui-disabled': { opacity: 0.4, borderColor: '#f2f4f7' }
                }}
            >
                <FirstPageIcon fontSize="small" />
            </IconButton>

            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
                size="small"
                sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    borderRadius: 1.5,
                    border: '1px solid #eaecf0',
                    color: '#64748b',
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#d0d5dd' },
                    '&.Mui-disabled': { opacity: 0.4, borderColor: '#f2f4f7' }
                }}
            >
                <KeyboardArrowLeft fontSize="small" />
            </IconButton>

            {pageNumbers.map((p, idx) => {
                if (p === '...') {
                    return (
                        <Box
                            key={`ellipsis-${idx}`}
                            sx={{
                                width: 28,
                                textAlign: 'center',
                                color: '#98a2b3',
                                fontSize: '0.8125rem',
                                userSelect: 'none',
                            }}
                        >
                            ...
                        </Box>
                    );
                }

                const isActive = p === currentPage;

                return (
                    <Button
                        key={p}
                        onClick={(e) => handlePageClick(e, p)}
                        size="small"
                        sx={{
                            minWidth: 32,
                            height: 32,
                            p: 0,
                            borderRadius: 1.5,
                            fontSize: '0.8125rem',
                            fontWeight: isActive ? 600 : 500,
                            color: isActive ? '#ffffff' : '#475467',
                            bgcolor: isActive ? 'primary.main' : 'transparent',
                            boxShadow: isActive ? '0 1px 2px rgba(var(--primary-color-rgb, 65, 70, 224), 0.2)' : 'none',
                            '&:hover': {
                                bgcolor: isActive ? 'primary.dark' : '#f1f5f9',
                                color: isActive ? '#ffffff' : '#101828',
                            },
                        }}
                    >
                        {p}
                    </Button>
                );
            })}

            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= totalPages - 1}
                aria-label="next page"
                size="small"
                sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    borderRadius: 1.5,
                    border: '1px solid #eaecf0',
                    color: '#64748b',
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#d0d5dd' },
                    '&.Mui-disabled': { opacity: 0.4, borderColor: '#f2f4f7' }
                }}
            >
                <KeyboardArrowRight fontSize="small" />
            </IconButton>

            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= totalPages - 1}
                aria-label="last page"
                size="small"
                sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    borderRadius: 1.5,
                    border: '1px solid #eaecf0',
                    color: '#64748b',
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#d0d5dd' },
                    '&.Mui-disabled': { opacity: 0.4, borderColor: '#f2f4f7' }
                }}
            >
                <LastPageIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}
